'use client';

import { parseAsString, useQueryState } from 'nuqs';
import { Icon } from '~/components/primitives';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import { AnimatePresence, motion } from 'motion/react';
import { useRef } from 'react';
import { cn } from '~/utils';
import { ACTIVITY_SEARCH_MODE_QUERY_PARAMS, ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS } from './shared';

const INPUT_TEXT_CLASSES =
    'text-[1.75rem] font-extrabold leading-[100%] sm:text-[2rem] md:text-[2.5rem] lg:text-[2.5rem] xl:text-[3.25rem]';

export const SearchTitle: React.FC<{
    defaultTitle: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}> = ({ defaultTitle, className, style }) => {
    const i18n = useI18n();
    // Use the shared search options: `scroll: false` stops the page jumping to the top when the
    // title becomes the search input (and on every keystroke), `shallow: true` keeps it client-side,
    // and `throttleMs` debounces the URL writes while typing.
    const [query, setQuery] = useQueryState(
        'query',
        parseAsString.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS),
    );
    const [mode] = useQueryState(
        'mode',
        ACTIVITY_SEARCH_MODE_QUERY_PARAMS.mode.withOptions(ACTIVITY_SEARCH_NUQS_DEFAULT_OPTIONS),
    );
    const isAiMode = (mode ?? 'normal') === 'ai';
    // Landing with `?query=` (the header's search entry) opens the input straight
    // away, focused, so the visitor types instead of finding the field first.
    const shouldFocusRef = useRef(query === '');

    /* min-h (not fixed h) so wrapped headlines aren't clipped on narrow viewports; keeps similar floor when switching to search */
    const titleBlockMinHeight =
        'min-h-[4rem] sm:min-h-[4rem] md:min-h-[4.5rem] lg:min-h-[5rem] xl:min-h-[5.75rem]';

    return (
        <div
            className={cn(
                'flex w-full min-w-0 max-w-full flex-col items-center',
                /* Bottom-align the headline so min-height reserves space upward (for wraps) without a large gap above the filter bar */
                query == null ? 'justify-end' : 'justify-center',
                titleBlockMinHeight,
            )}
        >
            <AnimatePresence mode="wait" initial={false}>
                {query == null ? (
                    <motion.div
                        key={isAiMode ? 'title-ai' : 'title-default'}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className={cn(
                            // In AI mode the sidebar is always open — nudge the hero title down a bit.
                            'flex w-full items-center justify-center transition-transform duration-200',
                            isAiMode && 'translate-y-3 sm:translate-y-4',
                        )}
                    >
                        <Title
                            as="h1"
                            size="4xl"
                            className={className}
                            style={style}
                            onClick={
                                isAiMode
                                    ? undefined
                                    : () => {
                                          shouldFocusRef.current = true;
                                          setQuery('');
                                      }
                            }
                        >
                            {isAiMode ? (
                                i18n._('activity.search.ai.heroTitle', undefined, {
                                    message: 'Prompt your search',
                                })
                            ) : (
                                <span className="cursor-text">{defaultTitle}</span>
                            )}
                        </Title>
                    </motion.div>
                ) : (
                    <motion.div
                        key="search"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="flex w-full min-w-0 max-w-full items-center justify-center"
                    >
                        {/*
                          Below sm: full-width input so placeholder/value isn’t clipped by an over-wide inline sizer.
                          sm+: invisible sizer + absolute input keeps the headline-style auto width.
                        */}
                        <div
                            className={cn(
                                'relative flex w-full min-w-0 max-w-full items-center justify-center gap-2',
                                'sm:inline-flex sm:w-auto sm:max-w-full sm:gap-0',
                            )}
                        >
                            <span
                                aria-hidden
                                className={cn(
                                    'invisible hidden whitespace-pre sm:inline',
                                    INPUT_TEXT_CLASSES,
                                )}
                                style={style}
                            >
                                {query ||
                                    i18n._('activity.search.placeholder', undefined, {
                                        message: 'Search activities',
                                    })}
                            </span>
                            <input
                                ref={el => {
                                    if (el && shouldFocusRef.current) {
                                        el.focus();
                                        shouldFocusRef.current = false;
                                    }
                                }}
                                type="text"
                                value={query}
                                onChange={e => setQuery(e.currentTarget.value)}
                                placeholder={i18n._('activity.search.placeholder', undefined, {
                                    message: 'Search activities',
                                })}
                                aria-label={i18n._('activity.search.placeholder', undefined, {
                                    message: 'Search activities',
                                })}
                                className={cn(
                                    INPUT_TEXT_CLASSES,
                                    'z-10 min-w-0 border-none bg-transparent text-center text-white outline-none ring-0 placeholder:text-white/40',
                                    'w-full flex-1 sm:absolute sm:inset-0 sm:w-full sm:flex-none',
                                )}
                                style={style}
                            />
                            {query.length > 0 ? (
                                <button
                                    type="button"
                                    onClick={() => setQuery('')}
                                    className="z-10 shrink-0 -translate-y-1 rounded-full bg-white/20 p-2 transition-colors hover:bg-white/30 sm:ml-12"
                                    aria-label={i18n._('activity.search.clear', undefined, {
                                        message: 'Clear search',
                                    })}
                                >
                                    <Icon name="close" className="text-white" size="xs" />
                                </button>
                            ) : (
                                <span aria-hidden className="sm:w-22 hidden shrink-0 sm:block" />
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
